'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useId, useMemo } from 'react';
import { CategoryAxis, ChartLiveRegion, ChartTooltip, DEFAULT_INTERACTION_LABELS, DEFAULT_SUMMARY_LABELS, ReferenceLine, ValueAxis, areaPath, colorStyle, createTickFormatter, createValueFormatter, createXFormatter, defaultChartColor, describeTrend, extent, isolatedPoints, labelIndices, linePath, linearScale, nearestIndex, numberAt, tickCountFor, useChartPointer, useElementWidth, useHiddenSeries, valueAxisLayout, } from '../../../internal/charts/index.js';
import { ChartFrame } from '../chart-frame/index.js';
const MARGIN = { top: 8, right: 12, bottom: 24 };
const FALLBACK_WIDTH = 640;
function isContinuous(v) {
    return v instanceof Date || (typeof v === 'number' && Number.isFinite(v));
}
/**
 * Line and area chart for trends over time or ordered categories, with
 * multiple series, gaps, reference lines, a shared crosshair tooltip on
 * hover, touch and keyboard, legend toggles and an accessible data table.
 */
export const LineChart = forwardRef(function LineChart({ data, xKey, xLabel, series, variant = 'line', curve = 'linear', xScale, locale, valueFormat, formatValue, formatTick, xFormat, formatX, includeZero = true, yDomain, referenceLines = [], dots = false, hiddenSeries, defaultHiddenSeries, onHiddenSeriesChange, legendToggle = true, activeIndex, onActiveIndexChange, onPointSelect, summary, labels, height = 240, status = 'ready', title, ...frame }, ref) {
    const hintId = useId();
    const il = { ...DEFAULT_INTERACTION_LABELS, ...labels };
    const sl = { ...DEFAULT_SUMMARY_LABELS, ...labels };
    const [hidden, toggle] = useHiddenSeries(hiddenSeries, defaultHiddenSeries, onHiddenSeriesChange);
    const [measureRef, measured] = useElementWidth();
    const width = measured ?? FALLBACK_WIDTH;
    const fmtValue = createValueFormatter(locale, valueFormat, formatValue);
    const fmtTick = createTickFormatter(locale, valueFormat, formatTick);
    const fmtX = createXFormatter(locale, xFormat, formatX);
    const colored = useMemo(() => series.map((s, i) => ({ ...s, color: (s.color ?? defaultChartColor(i)) })), [series]);
    const visible = colored.filter((s) => !hidden.includes(s.key));
    const n = data.length;
    const xs = data.map((d) => d[xKey]);
    const continuous = (xScale ?? (xs.every(isContinuous) ? 'time' : 'category')) === 'time';
    const xLabels = xs.map((v, i) => fmtX(v, i));
    const values = (key) => data.map((d) => numberAt(d, key));
    // Value axis.
    const plotH = Math.max(40, height - MARGIN.top - MARGIN.bottom);
    const ext = extent([
        ...visible.flatMap((s) => values(s.key)),
        ...referenceLines.map((r) => r.value),
    ]);
    const axis = valueAxisLayout(ext, {
        includeZero,
        domain: yDomain,
        tickCount: tickCountFor(plotH),
        format: fmtTick,
    });
    const left = axis.labelWidth + 8;
    const x0 = left;
    const x1 = Math.max(x0 + 1, width - MARGIN.right);
    const y0 = MARGIN.top;
    const y1 = MARGIN.top + plotH;
    const y = linearScale(axis.domain, [y1, y0]);
    // X positions.
    const positions = useMemo(() => {
        if (n === 0)
            return [];
        if (n === 1)
            return [(x0 + x1) / 2];
        if (continuous) {
            const t = xs.map((v) => (v instanceof Date ? v.getTime() : Number(v)));
            const lo = Math.min(...t);
            const hi = Math.max(...t);
            const sx = linearScale([lo, hi === lo ? lo + 1 : hi], [x0, x1]);
            return t.map(sx);
        }
        return xs.map((_, i) => x0 + (i / (n - 1)) * (x1 - x0));
    }, [n, continuous, xs, x0, x1]);
    const indexAt = useCallback((px) => nearestIndex(positions, px), [positions]);
    const { active, source, plotProps } = useChartPointer({
        count: status === 'ready' ? n : 0,
        activeIndex,
        onActiveIndexChange,
        indexAt,
        onSelect: onPointSelect ? (i) => onPointSelect(data[i] ?? {}, i) : undefined,
    });
    const pointsFor = (key) => data.map((d, i) => {
        const v = numberAt(d, key);
        return v === null ? null : { x: positions[i] ?? 0, y: y(v) };
    });
    const baseline = y(Math.max(axis.domain[0], Math.min(0, axis.domain[1])));
    const ticks = labelIndices(n, x1 - x0).map((i) => ({
        key: i,
        at: positions[i] ?? 0,
        label: xLabels[i] ?? '',
        anchor: (n > 1 && i === 0 ? 'start' : n > 1 && i === n - 1 ? 'end' : 'middle'),
    }));
    const showDot = (v) => v !== null;
    const describe = (i) => `${xLabels[i]}: ${visible
        .map((s) => {
        const v = numberAt(data[i], s.key);
        return `${s.label} ${v === null ? il.noValue : fmtValue(v)}`;
    })
        .join(', ')}`;
    const generated = n === 0
        ? sl.noData
        : [
            sl.points(n, xLabels[0] ?? '', xLabels[n - 1] ?? ''),
            ...visible.map((s) => describeTrend(s.label, values(s.key), fmtValue, sl)),
            ...referenceLines.map((r) => `${r.label ?? ''} ${fmtValue(r.value)}`.trim()),
        ].join('. ');
    const table = {
        columns: [
            { key: '__x', label: xLabel ?? xKey },
            ...colored.map((s) => ({ key: s.key, label: s.label })),
        ],
        rows: data.map((d, i) => {
            const row = { __x: xLabels[i] ?? '' };
            for (const s of colored) {
                const v = numberAt(d, s.key);
                row[s.key] = v === null ? il.noValue : fmtValue(v);
            }
            return row;
        }),
    };
    const legend = colored.length > 1
        ? colored.map((s) => ({
            key: s.key,
            label: s.label,
            color: s.color,
            dashed: s.dashed ?? false,
            hidden: hidden.includes(s.key),
        }))
        : undefined;
    const activeX = active !== null ? positions[active] : undefined;
    return (_jsx(ChartFrame, { ref: ref, ...frame, title: title, status: n === 0 && status === 'ready' ? 'empty' : status, height: height, legend: legend, legendMarker: "line", onLegendToggle: legendToggle && legend ? toggle : undefined, table: table, labels: labels, "data-chart": "line", children: _jsxs("div", { ref: measureRef, className: "mn-chart__plot mn-line-chart", "data-variant": variant, role: "group", "aria-roledescription": il.chart, "aria-label": summary ?? generated, "aria-describedby": hintId, ...plotProps, children: [_jsxs("svg", { className: "mn-chart__svg", viewBox: `0 0 ${width} ${height}`, height: height, "aria-hidden": "true", focusable: "false", children: [_jsx(ValueAxis, { ticks: axis.ticks, scale: y, format: fmtTick, orientation: "vertical", from: x0, to: x1, labelAt: x0 - 8 }), _jsx(CategoryAxis, { labels: ticks, orientation: "horizontal", position: y1 + 8 }), variant === 'area' &&
                            visible.map((s) => (_jsx("path", { className: "mn-chart__area", style: colorStyle(s.color), d: areaPath(pointsFor(s.key), baseline, curve) }, `area-${s.key}`))), referenceLines.map((r, i) => (_jsx(ReferenceLine, { line: r, scale: y, orientation: "vertical", from: x0, to: x1, format: fmtTick }, `ref-${i}`))), activeX !== undefined && (_jsx("line", { className: "mn-chart__crosshair", x1: activeX, x2: activeX, y1: y0, y2: y1 })), visible.map((s) => {
                            const pts = pointsFor(s.key);
                            const lone = dots ? pts.filter((p) => p !== null) : isolatedPoints(pts);
                            return (_jsxs("g", { style: colorStyle(s.color), "data-series": s.key, children: [_jsx("path", { className: "mn-chart__line", "data-dashed": s.dashed || undefined, d: linePath(pts, curve) }), lone.map((p, i) => (_jsx("circle", { className: "mn-chart__dot", cx: p.x, cy: p.y, r: 3.5 }, i))), active !== null && showDot(numberAt(data[active], s.key)) && (_jsx("circle", { className: "mn-chart__dot", "data-active": "", cx: positions[active], cy: y(numberAt(data[active], s.key) ?? 0), r: 5 }))] }, s.key));
                        })] }), active !== null && activeX !== undefined && (_jsx(ChartTooltip, { x: activeX, y: y0, width: width, title: xLabels[active], rows: visible.map((s) => {
                        const v = numberAt(data[active], s.key);
                        return {
                            key: s.key,
                            label: s.label,
                            value: v === null ? il.noValue : fmtValue(v),
                            color: s.color,
                            dashed: s.dashed ?? false,
                            marker: 'line',
                        };
                    }) })), _jsx("span", { id: hintId, className: "mn-visually-hidden", children: il.keyboardHint }), _jsx(ChartLiveRegion, { children: active !== null && source === 'keyboard' ? describe(active) : '' })] }) }));
});
//# sourceMappingURL=line-chart.js.map